import { createPageShell } from '../ui/pageShell.js';
import { NATIONALITIES } from '@multirx/shared/types/nationalities.js';
import { getCurrentUser, login, register } from './session.js';
import { renderNavAccount } from './navAccount.js';
import { mountAccountProfileView } from './accountProfileView.js';
import type { AuthUser } from '@multirx/shared/types/user.js';

function goToDashboard(): void {
  window.location.hash = 'accueil';
}

/** Champs texte/email/password/select uniquement : jamais de File dans ces formulaires. */
function formField(data: FormData, name: string): string {
  return (data.get(name) as string | null) ?? '';
}

export function mountAuthApp(root: HTMLElement): void {
  render();

  function render(): void {
    root.innerHTML = '';
    root.classList.add('app-view', 'circuits-app');
    const user = getCurrentUser();
    if (user) renderLoggedIn(user);
    else renderLoginRegister();
  }

  function renderLoggedIn(user: AuthUser): void {
    const { page, header, body } = createPageShell({
      title: 'Mon paddock',
      breadcrumbs: [{ label: 'Tableau de bord', href: '#dashboard' }, { label: 'Mon paddock' }],
      wide: true,
    });
    const titles = header.querySelector('.page-header-row > div');
    if (titles) {
      titles.classList.add('page-title-with-badge');
      const badge = document.createElement('span');
      badge.className = 'account-email-badge';
      badge.textContent = user.email;
      badge.title = `Connecté en tant que ${user.email}`;
      titles.appendChild(badge);
    }
    mountAccountProfileView(body, user);
    root.appendChild(page);
  }

  function renderLoginRegister(): void {
    const { page, body } = createPageShell({
      title: 'Connexion',
      subtitle: 'Connectez-vous ou créez un compte pour jouer votre propre pilote.',
      breadcrumbs: [{ label: 'Tableau de bord', href: '#dashboard' }, { label: 'Connexion' }],
    });

    const section = document.createElement('section');
    section.className = 'card settings-card auth-card';
    section.innerHTML = `
      <div class="auth-tabswitch">
        <input hidden class="auth-tabswitch-rd1" name="auth-tabswitch" id="auth-tabswitch-login" type="radio" checked />
        <label class="auth-tabswitch-label" for="auth-tabswitch-login"><span>Connexion</span></label>
        <input hidden class="auth-tabswitch-rd2" name="auth-tabswitch" id="auth-tabswitch-register" type="radio" />
        <label class="auth-tabswitch-label" for="auth-tabswitch-register"><span>Créer un compte</span></label>
        <div class="auth-tabswitch-bar"></div>
        <div class="auth-tabswitch-slidebar"></div>
      </div>
      <form class="auth-form" data-form="login">
        <div class="field brutalist-container">
          <input class="brutalist-input" type="email" id="auth-login-email" name="email" placeholder="VOTRE EMAIL" required autocomplete="email" />
          <label class="brutalist-label" for="auth-login-email">Email</label>
        </div>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="password" id="auth-login-password" name="password" placeholder="••••••••" required autocomplete="current-password" />
          <label class="brutalist-label" for="auth-login-password">Mot de passe</label>
        </div>
        <p class="auth-error" role="alert" hidden></p>
        <button type="submit" class="btn btn-primary auth-submit">Se connecter</button>
      </form>
      <form class="auth-form" data-form="register" hidden>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="email" id="auth-register-email" name="email" placeholder="VOTRE EMAIL" required autocomplete="email" />
          <label class="brutalist-label" for="auth-register-email">Email</label>
        </div>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="password" id="auth-register-password" name="password" placeholder="8 CARACTÈRES MIN." required minlength="8" autocomplete="new-password" />
          <label class="brutalist-label" for="auth-register-password">Mot de passe</label>
        </div>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="text" id="auth-register-firstname" name="firstName" placeholder="PRÉNOM" required />
          <label class="brutalist-label" for="auth-register-firstname">Prénom</label>
        </div>
        <div class="field brutalist-container">
          <input class="brutalist-input" type="text" id="auth-register-lastname" name="lastName" placeholder="NOM" required />
          <label class="brutalist-label" for="auth-register-lastname">Nom</label>
        </div>
        <div class="field brutalist-container">
          <select class="brutalist-input brutalist-select" id="auth-register-nationality" name="nationality" required></select>
          <label class="brutalist-label" for="auth-register-nationality">Nationalité</label>
        </div>
        <p class="auth-error" role="alert" hidden></p>
        <button type="submit" class="btn btn-primary auth-submit">Créer mon compte</button>
      </form>
    `;

    const tabRadios = {
      login: section.querySelector<HTMLInputElement>('#auth-tabswitch-login')!,
      register: section.querySelector<HTMLInputElement>('#auth-tabswitch-register')!,
    };
    const forms = {
      login: section.querySelector<HTMLFormElement>('[data-form="login"]')!,
      register: section.querySelector<HTMLFormElement>('[data-form="register"]')!,
    };
    function syncFormForTab(): void {
      forms.login.hidden = !tabRadios.login.checked;
      forms.register.hidden = !tabRadios.register.checked;
    }
    tabRadios.login.addEventListener('change', syncFormForTab);
    tabRadios.register.addEventListener('change', syncFormForTab);

    const nationalitySelect = forms.register.querySelector('select[name="nationality"]')!;
    NATIONALITIES.forEach((entry) => {
      const opt = document.createElement('option');
      opt.value = entry.code;
      opt.textContent = `${entry.flagEmoji} ${entry.label}`;
      nationalitySelect.appendChild(opt);
    });

    function showError(form: HTMLFormElement, message: string): void {
      const p = form.querySelector<HTMLElement>('.auth-error')!;
      p.textContent = message;
      p.hidden = false;
    }

    forms.login.addEventListener('submit', (e) => {
      e.preventDefault();
      const data = new FormData(forms.login);
      void login({
        email: formField(data, 'email'),
        password: formField(data, 'password'),
      })
        .then(() => {
          renderNavAccount();
          goToDashboard();
        })
        .catch((err: Error) => showError(forms.login, err.message));
    });

    forms.register.addEventListener('submit', (e) => {
      e.preventDefault();
      const data = new FormData(forms.register);
      void register({
        email: formField(data, 'email'),
        password: formField(data, 'password'),
        firstName: formField(data, 'firstName'),
        lastName: formField(data, 'lastName'),
        nationality: formField(data, 'nationality'),
      })
        .then((user) => {
          renderNavAccount();
          renderOnboarding(user);
        })
        .catch((err: Error) => showError(forms.register, err.message));
    });

    body.appendChild(section);
    root.appendChild(page);
  }

  function renderOnboarding(user: AuthUser): void {
    root.innerHTML = '';
    root.classList.add('app-view', 'circuits-app');
    const { page, body } = createPageShell({
      title: 'Bienvenue !',
      subtitle: 'Personnalisez votre pilote et votre voiture avant de rejoindre la piste.',
      wide: true,
    });

    mountAccountProfileView(body, user);

    const doneBtn = document.createElement('button');
    doneBtn.type = 'button';
    doneBtn.className = 'btn btn-primary';
    doneBtn.textContent = 'Terminer — aller au tableau de bord';
    doneBtn.style.marginTop = '1rem';
    doneBtn.addEventListener('click', goToDashboard);

    body.appendChild(doneBtn);
    root.appendChild(page);
  }
}
